// The ECM client asks MSAL for a fresh token on every request (acquireTokenSilent), as the graduate
// dashboard does. ../api/axios sends the sessionStorage token from sign-in, which is never refreshed:
// it expires within the hour, and a long editing session would get 401 at Publish and lose the draft.
import { apiClient as axios } from './ecm-api';
import { formatMoment, parseServerTime } from './server-time';

/**
 * SIMS-owned SendGrid dynamic templates (backend: the EMAIL TEMPLATES section of function_app.py,
 * rules in utils/email_templates.py, orchestration in utils/email_template_saga.py).
 *
 * Every call goes through ecm-api's apiClient, so a fresh bearer token is attached. The server is
 * the gate for everything here - roles, the template key, the shared-account denylist - and the
 * role sets below only decide which buttons this screen shows.
 *
 * The SendGrid account is shared with the new-student password emails and the website-lead
 * notifications, so nothing in this file can name a SendGrid template SIMS does not manage: every
 * write takes a SIMS template id (a GUID), never a d- id, apart from adopt, which the server only
 * allows for templates SIMS already sends with and still references.
 */

/**
 * The backend's template purposes (utils/email_templates.py). 'campaign' has had no screen since
 * 2026-10-06 (staff create campaign templates in SendGrid); it stays here as the server's contract
 * until a follow-up retires it in frontend and backend together.
 */
export type Purpose = 'campaign' | 'drip_layout';

export const PURPOSES: Purpose[] = ['campaign', 'drip_layout'];

export const PURPOSE_LABEL: Record<Purpose, string> = {
  campaign: 'Campaign email',
  drip_layout: 'Drip layout',
};

/** Same sets as the server's ROLES (read and write are the same per purpose). */
export const PURPOSE_ROLES: Record<Purpose, string[]> = {
  campaign: ['Survey.Admin', 'Campaign.Manager'],
  drip_layout: ['Survey.Admin', 'Drip.Manager'],
};

/** Adopt and the SendGrid health check are Survey.Admin only. */
export const TEMPLATE_ADMIN_ROLES = ['Survey.Admin'];

export function canUsePurpose(roles: string[], purpose: Purpose | null | undefined): boolean {
  return !!purpose && PURPOSE_ROLES[purpose].some((r) => roles.includes(r));
}

export function isTemplateAdmin(roles: string[]): boolean {
  return TEMPLATE_ADMIN_ROLES.some((r) => roles.includes(r));
}

/** A validation error or warning. The server names the construct and, where it can, the line. */
export interface TemplateIssue {
  code: string;
  name?: string;
  context?: string;
  line?: number | null;
  message: string;
}

export interface SetupState {
  /** SENDGRID_TEMPLATES_API_KEY exists. Without it every write answers 503 "not set up yet". */
  templatesKeyConfigured: boolean;
  /** SENDGRID_API_KEY exists. Test sends need it. */
  mailKeyConfigured: boolean;
}

export interface TemplateDrift {
  kind: 'edited' | 'activated_other' | 'versions_missing';
  sgVersionId: string | null;
  sgUpdatedAt: string | null;
  versionNo: number | null;
  versionDocId?: string | null;
  detectedAt?: string | null;
  detectedBy?: string | null;
}

export interface PendingOp {
  opId: string | null;
  kind: 'create' | 'publish' | 'rollback' | null;
  state: string | null;
  /** SendGrid did not answer in time, so the change may already be live. */
  uncertain: boolean;
  startedAt: string | null;
  startedBy: string | null;
}

export interface TemplateLastError {
  at?: string | null;
  step?: string | null;
  httpStatus?: number | null;
  code?: string | null;
  message?: string | null;
}

export interface TemplatePublic {
  id: string;
  name: string;
  purpose: Purpose;
  origin: 'sims' | 'adopted' | null;
  status: 'draft' | 'active' | 'archived';
  archivedReason: 'user' | 'create_not_applied' | null;
  syncState: 'synced' | 'pending' | 'drift' | 'missing';
  sgTemplateId: string | null;
  sgTemplateName: string | null;
  activeVersionNo: number | null;
  previousVersionNo: number | null;
  latestVersionNo: number;
  hasRollback: boolean;
  versionsMissing?: boolean;
  subject: string | null;
  htmlBytes: number;
  plainBytes: number;
  needsFix: boolean;
  issues: TemplateIssue[];
  drift: TemplateDrift | null;
  pendingOp: PendingOp | null;
  lastError: TemplateLastError | null;
  isDripDefault: boolean;
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
  /** Echo this back on publish, rollback and archive. A mismatch is 409 stale. */
  etag: string | null;
}

export type VersionSource = 'sims_publish' | 'adopted_import' | 'captured_external_edit';

export interface VersionSummary {
  versionNo: number | null;
  source: VersionSource | null;
  state: string | null;
  sgVersionId: string | null;
  subject: string | null;
  createdAt: string | null;
  createdBy: string | null;
  publishedAt: string | null;
  /** SIMS's last publish. During drift SendGrid sends a console version instead: see isLiveInSendGrid. */
  isActive: boolean;
  isPrevious: boolean;
  /** What SendGrid sends now: the console's captured version during drift, else the active one. */
  isLiveInSendGrid?: boolean;
  /** False when a captured or adopted version was too large to keep; only its metadata exists. */
  contentStored: boolean;
}

export interface VersionDetail extends VersionSummary {
  html: string | null;
  plain: string | null;
  plainGenerated: boolean;
  sgEditor: string | null;
  note: string | null;
  /** Built by the server with the preview CSP. Captured console HTML is untrusted. */
  previewDocument: string | null;
}

/** Counts by status and capped ids only - never campaign titles, never recipients (FERPA). */
export interface TemplateUsage {
  campaigns: { total: number; byStatus: Record<string, number>; ids: string[] };
  dripEntries: { total: number; byStatus: Record<string, number>; ids: string[]; inheritsDefault: boolean };
  isDripDefault: boolean;
  isGraduateDefault: boolean;
  sendingNow: boolean;
  live: boolean;
  requiresConfirm?: boolean;
}

export interface CatalogKey {
  name: string;
  description: string;
  sample: string | null;
  contexts: Array<'html' | 'subject' | 'plain'>;
  /** Written with triple braces: {{{body}}} in a drip layout's HTML, {{{body_text}}} in its plain text. */
  raw: boolean;
  /** May start a link's href. */
  url: boolean;
}

export interface PurposeCatalog {
  keys: CatalogKey[];
  rules: string[];
  limits: { nameChars: number; subjectChars: number; htmlBytes: number; plainBytes: number; gmailClipBytes: number };
  unsubscribeTags: string[];
}

export interface TemplateCatalog {
  /** Only the purposes the caller can use. */
  purposes: Partial<Record<Purpose, PurposeCatalog>>;
  syntax: { allowed: string[]; refused: string[] };
  setup: SetupState;
}

export interface RenderedPreview {
  subject: string;
  html: string;
  plain: string;
  /** The HTML with the CSP meta inserted after <head>, for a sandbox="" iframe. Never innerHTML. */
  previewDocument: string;
}

export interface PreviewResult {
  ok: boolean;
  errors: TemplateIssue[];
  warnings: TemplateIssue[];
  keysUsed: string[];
  sizes: { htmlBytes: number; plainBytes: number };
  rendered: RenderedPreview | null;
}

export interface ActiveVersion {
  versionNo: number | null;
  subject: string | null;
  html: string | null;
  plain: string | null;
  contentStored: boolean;
  previewDocument: string | null;
}

export interface TemplateDetail {
  template: TemplatePublic;
  activeVersion: ActiveVersion | null;
  versions: VersionSummary[];
  usage: TemplateUsage;
  /** ok null: not checked (pending change, archived, or never reached SendGrid). */
  driftCheck: { ok: boolean | null; checkedAt: string | null; reason: string | null };
}

export interface TestSendResult {
  ok: boolean;
  to: string;
  statusCode: number;
  messageId?: string | null;
  sentInLastHour: number;
  limitPerHour: number;
}

/**
 * What create, publish and rollback answer. A 202 carries code "may_be_live": SendGrid did not
 * answer in time, the change may already be live, and Check now (reconcile) settles it.
 */
export interface TemplateWriteResult {
  template: TemplatePublic;
  version?: { versionNo: number | null; sgVersionId: string | null };
  pruned?: number;
  warnings?: TemplateIssue[];
  code?: 'may_be_live';
  error?: string;
  opId?: string;
}

export interface ReconcileResult {
  /** superseded: SendGrid holds SIMS's version, but the version before it was made live again in
   *  the console, unchanged. Reconcile never re-activates over that; publish again if still meant. */
  state: 'committed' | 'not_applied' | 'nothing_pending' | 'superseded';
  template: TemplatePublic;
}

export interface AdoptCandidate {
  sgTemplateId: string;
  referencedBy: { setting: string | null; campaigns: number; dripEntries: number };
  allowedPurposes: Purpose[];
}

export interface AdoptInspection {
  sgName: string | null;
  generation: string | null;
  subject: string | null;
  activeVersionId: string | null;
  activeUpdatedAt: string | null;
  versionCount: number;
  editor: string | null;
  /** Problems the first SIMS publish must fix. Adoption itself never refuses for these. */
  issues: TemplateIssue[];
  warnings: TemplateIssue[];
  sizes: { htmlBytes: number; plainBytes: number };
  usage: TemplateUsage;
  previewDocument: string | null;
  warning?: string;
  editorNote?: string;
}

export interface TemplateHealth {
  templatesKey: 'set' | 'missing';
  mailKey: 'set' | 'missing';
  scopes: { checked: boolean; ok: boolean; missing: string[]; forbidden: string[]; unexpected: string[]; error: string | null };
  budget: {
    limit: number; reserve: number; slots: number | null; versionsKnown: boolean | null;
    templates: number | null; simsTemplates: number | null; simsCap: number;
    checkedAt: string | null; complete?: boolean; error: string | null;
  };
  denylist: string[];
}

// ── errors ──────────────────────────────────────────────────────────────────────────────────────

/** A refused or failed call, flattened so a screen can branch on `code` and show `error`. */
export interface TemplateApiError {
  /** HTTP status; 0 when SIMS could not be reached at all. */
  status: number;
  code: string;
  /** The sentence to show. */
  error: string;
  /** Seconds, from the Retry-After header, when the server sent one. */
  retryAfter?: number;
  /** The whole JSON body: drift, etag, usage, errors, ids... depending on the code. */
  body: Record<string, any>;
}

/**
 * Sentences for each code, used only when the server sent none (the server's own sentence wins:
 * for templates_key_scopes, for instance, it names the scopes). Mirrors MESSAGES in
 * utils/email_templates.py.
 */
export const TEMPLATE_CODE_MESSAGES: Record<string, string> = {
  templates_key_missing: 'Email template editing is not set up yet. An administrator must add the SendGrid template key.',
  templates_key_scopes: 'The SendGrid template key does not have exactly the access SIMS allows.',
  scopes_unavailable: 'SIMS could not check the SendGrid template key. Try again in a minute.',
  templates_key_rejected: 'SendGrid rejected the template key. An administrator must check it.',
  budget_unavailable: 'SIMS could not count the templates in SendGrid, so it will not create one. Try again in a minute.',
  sendgrid_budget: 'The shared SendGrid account is close to its template limit; SIMS keeps room for the other systems that use it.',
  sims_cap: 'SIMS already has its maximum number of templates in SendGrid. An administrator can delete archived ones in the SendGrid console to make room.',
  sendgrid_limit: 'SendGrid refused: the account has reached its template or version limit.',
  sendgrid_rejected: 'SendGrid refused the change.',
  sendgrid_rate_limited: 'SendGrid is asking SIMS to slow down. Try again shortly.',
  sendgrid_unavailable: 'SendGrid did not answer. Nothing was changed; try again in a minute.',
  sendgrid_uncertain: 'SendGrid did not answer in time; the test email may still arrive.',
  sendgrid_refused: 'SendGrid refused the send.',
  deadline: 'SendGrid is answering too slowly to finish safely. Nothing was changed; try again.',
  may_be_live: 'SendGrid did not answer in time. The new version may already be live. Press Check now.',
  draft_created: 'SendGrid made the template but not its first version, so nothing is live yet. Press Publish to finish it; do not create it again.',
  stale: 'Someone else changed this template. Reload it and try again.',
  op_pending: 'SendGrid has not confirmed the last change. Press Check now first.',
  archived: 'This template is archived.',
  not_created: 'This template is not in SendGrid yet. Press Check now.',
  sending_now: 'A campaign using this template is sending now; publish when it finishes.',
  confirm_live: 'Emails sent through SendGrid with this template from now on use this version. Resends from the campaign page do not: they send the copy saved when each campaign was created.',
  drift: 'This template was changed in the SendGrid console. That version is saved in history; publish over it only if you mean to replace it.',
  needs_reconcile: 'SendGrid shows a version SIMS has not confirmed yet. Press Check now first.',
  missing_in_sendgrid: 'This template no longer exists in SendGrid.',
  rollback_unavailable: 'There is no earlier version to roll back to.',
  rollback_target_missing: 'The previous version is no longer in SendGrid; load it from history and publish it instead.',
  rollback_target_edited: 'The previous version was edited in the SendGrid console after SIMS saved it. That edit is saved in history; roll back to it only if you mean to make it live.',
  name_taken: 'Another template of this kind already has that name.',
  in_use: 'This template is in use; it cannot be archived.',
  busy: 'Another check is already running for this template.',
  too_soon: 'SendGrid may still be applying the change. Check again in a minute.',
  ambiguous: 'SendGrid holds more than one template with this template\'s SIMS tag. An administrator must resolve it in the SendGrid console.',
  not_found: 'Template not found.',
  invalid: 'The template has problems to fix first.',
  test_send_cap: 'That is the hourly limit of test sends. Wait a little before sending more.',
  rate_check_failed: 'Could not verify the test-send limit.',
  mail_key_missing: 'SendGrid is not configured for sending (SENDGRID_API_KEY).',
  from_missing: 'The sender address is not configured (FROM_EMAIL).',
  denylisted: 'SIMS does not manage that template.',
  unauthorized: 'Your sign-in has expired. Sign in again.',
  forbidden: 'You do not have permission to do that.',
  container_missing: 'Email templates are not set up yet: the drip_campaigns container does not exist.',
  unreachable: 'SIMS could not be reached. Check your connection and try again.',
  internal: 'Something went wrong.',
};

export function toTemplateApiError(err: any, fallback: string): TemplateApiError {
  const response = err?.response;
  if (!response) {
    return { status: 0, code: 'unreachable', error: TEMPLATE_CODE_MESSAGES.unreachable, body: {} };
  }
  const status: number = response.status ?? 0;
  const body: Record<string, any> =
    response.data && typeof response.data === 'object' && !Array.isArray(response.data) ? response.data : {};
  let code: string = typeof body.code === 'string' ? body.code : '';
  // _require_roles and the missing-container response carry a sentence but no code.
  if (!code && status === 401) code = 'unauthorized';
  else if (!code && status === 403) code = 'forbidden';
  else if (!code && status === 503 && typeof body.message === 'string') code = 'container_missing';
  else if (!code) code = status >= 500 ? 'internal' : 'unknown';
  let error: string = typeof body.error === 'string' && body.error ? body.error : '';
  if (code === 'container_missing' && typeof body.message === 'string') {
    error = `${error ? `${error}. ` : ''}${body.message}`;
  }
  if (!error) error = TEMPLATE_CODE_MESSAGES[code] || fallback;
  const header = response.headers?.['retry-after'];
  const retryAfter = header !== undefined && header !== null && header !== ''
    ? Number(header) : (typeof body.retryAfter === 'number' ? body.retryAfter : undefined);
  return { status, code, error, body, retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined };
}

// ── reads ───────────────────────────────────────────────────────────────────────────────────────

export async function getTemplateCatalog(): Promise<TemplateCatalog> {
  const { data } = await axios.get('/email-templates/catalog');
  return data;
}

export async function listEmailTemplates(opts: { purpose?: Purpose | null; archived?: boolean } = {}
): Promise<{ templates: TemplatePublic[]; setup: SetupState }> {
  const params: Record<string, string> = {};
  if (opts.purpose) params.purpose = opts.purpose;
  if (opts.archived) params.archived = 'true';
  const { data } = await axios.get('/email-templates', { params });
  return { templates: Array.isArray(data?.templates) ? data.templates : [], setup: data?.setup };
}

/** One template, its live content, history and usage. The server checks SendGrid for console edits. */
export async function getEmailTemplate(id: string, driftCheck = true): Promise<TemplateDetail> {
  const { data } = await axios.get(`/email-templates/${encodeURIComponent(id)}`,
    { params: driftCheck ? undefined : { driftCheck: 'false' } });
  return data;
}

export async function getEmailTemplateVersion(id: string, versionNo: number): Promise<VersionDetail> {
  const { data } = await axios.get(
    `/email-templates/${encodeURIComponent(id)}/versions/${encodeURIComponent(String(versionNo))}`);
  return data.version;
}

export async function getAdoptableTemplates(): Promise<AdoptCandidate[]> {
  const { data } = await axios.get('/email-templates/adoptable');
  return Array.isArray(data?.candidates) ? data.candidates : [];
}

export async function getTemplateHealth(): Promise<TemplateHealth> {
  const { data } = await axios.get('/email-templates/health');
  return data;
}

// ── preview and test send (neither needs the template key) ─────────────────────────────────────

export interface TemplateDraft {
  purpose: Purpose;
  subject: string;
  html: string;
  /** Campaign only; blank means SIMS generates it from the HTML. Never sent for a drip layout. */
  plain?: string;
}

/** Validate and render with synthetic sample data. No writes and no SendGrid call. */
export async function previewEmailTemplate(draft: TemplateDraft & { loadImages?: boolean }): Promise<PreviewResult> {
  const { data } = await axios.post('/email-templates/preview', draft);
  return data;
}

/** ONE email, to the signed-in user's own staff address only. There is no address field. */
export async function sendTemplateTest(
  body: ({ mode: 'draft' } & TemplateDraft) | { mode: 'live'; templateId: string },
): Promise<TestSendResult> {
  const { data } = await axios.post('/email-templates/test-send', body);
  return data;
}

// ── writes (each answers 503 templates_key_missing until the key exists) ───────────────────────

export async function createEmailTemplate(body: TemplateDraft & { name: string }): Promise<TemplateWriteResult> {
  const { data } = await axios.post('/email-templates', body);
  return data;
}

export interface PublishOptions {
  etag: string | null;
  /** The caller saw the usage counts and confirmed. */
  confirmLive?: boolean;
  /** Replace exactly this console version; the server refuses if SendGrid now shows another. */
  overwriteDrift?: { sgVersionId: string | null; sgUpdatedAt: string | null };
  /** Rollback only: make live exactly this console edit of the previous version (from the 409). */
  confirmTargetEdited?: { sgVersionId: string | null; sgUpdatedAt: string | null };
}

export async function publishEmailTemplate(
  id: string, body: { subject: string; html: string; plain?: string } & PublishOptions,
): Promise<TemplateWriteResult> {
  const { data } = await axios.post(`/email-templates/${encodeURIComponent(id)}/publish`, body);
  return data;
}

export async function rollbackEmailTemplate(id: string, body: PublishOptions): Promise<TemplateWriteResult> {
  const { data } = await axios.post(`/email-templates/${encodeURIComponent(id)}/rollback`, body);
  return data;
}

/** Check now: settle a change SendGrid never confirmed. Never re-sends a version. */
export async function reconcileEmailTemplate(id: string): Promise<ReconcileResult> {
  const { data } = await axios.post(`/email-templates/${encodeURIComponent(id)}/reconcile`, {});
  return data;
}

/** SIMS-only; the SendGrid template stays sendable. */
export async function archiveEmailTemplate(id: string, archived: boolean, etag: string | null
): Promise<{ template: TemplatePublic }> {
  const { data } = await axios.post(`/email-templates/${encodeURIComponent(id)}/archive`, { archived, etag });
  return data;
}

/** confirm false inspects (read only); confirm true records the adoption. Neither writes to SendGrid. */
export async function adoptEmailTemplate(body: {
  sgTemplateId: string; purpose: Purpose; name?: string; confirm: boolean; expectedSgVersionId?: string | null;
}): Promise<AdoptInspection | { template: TemplatePublic }> {
  const { data } = await axios.post('/email-templates/adopt', body);
  return data;
}

// ── display helpers ─────────────────────────────────────────────────────────────────────────────

export type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

export const STATUS_COLOR: Record<string, ChipColor> = {
  draft: 'default', active: 'success', archived: 'default',
};

export function statusLabel(t: TemplatePublic): string {
  if (t.status === 'archived') {
    return t.archivedReason === 'create_not_applied' ? 'Archived (never reached SendGrid)' : 'Archived';
  }
  return t.status === 'active' ? 'Active' : 'Draft';
}

/** One chip saying whether SendGrid and SIMS agree. */
export function syncLabel(t: TemplatePublic): { label: string; color: ChipColor; help: string } {
  if (t.pendingOp?.uncertain) {
    return { label: 'May be live', color: 'warning',
      help: 'SendGrid did not confirm the last change. It may already be live. Open it and press Check now.' };
  }
  if (t.pendingOp || t.syncState === 'pending') {
    return { label: 'Pending', color: 'info', help: 'A change is being applied in SendGrid.' };
  }
  if (t.syncState === 'missing') {
    return { label: 'Missing', color: 'error', help: 'This template no longer exists in SendGrid.' };
  }
  if (t.syncState === 'drift') {
    return { label: 'Changed in SendGrid', color: 'warning',
      help: 'Someone changed it in the SendGrid console. That version is saved in history.' };
  }
  if (!t.sgTemplateId) {
    return { label: 'Not in SendGrid', color: 'default', help: 'SIMS has not created this template in SendGrid.' };
  }
  return { label: 'Synced', color: 'success', help: 'SendGrid shows what SIMS last published.' };
}

export function formatWhen(iso: string | null | undefined): string {
  // The server writes naive UTC ISO strings (datetime.utcnow().isoformat()), and SendGrid's
  // updated_at uses a space; server-time reads both as UTC, the one rule every drip screen uses.
  const d = parseServerTime(iso);
  return d ? formatMoment(d) : '—';
}

export function formatBytes(n: number | null | undefined): string {
  const v = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  if (v < 1024) return `${v} B`;
  return `${(v / 1024).toFixed(v < 10 * 1024 ? 1 : 0)} KB`;
}

/** UTF-8 byte length, the unit of the server's size limits. */
export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

export const VERSION_SOURCE_LABEL: Record<VersionSource, string> = {
  sims_publish: 'SIMS',
  adopted_import: 'Adopted',
  captured_external_edit: 'Edited in SendGrid',
};

/** "Used by 3 campaigns (1 active) and 2 drip entries" - counts only, never titles. */
export function describeUsage(usage: TemplateUsage | null | undefined): string {
  if (!usage) return 'Usage unknown.';
  const statusText = (by: Record<string, number>) => {
    const parts = Object.entries(by || {}).map(([s, n]) => `${n} ${s}`);
    return parts.length ? ` (${parts.join(', ')})` : '';
  };
  const c = usage.campaigns?.total ?? 0;
  const d = usage.dripEntries?.total ?? 0;
  const pieces = [
    `${c} campaign${c === 1 ? '' : 's'}${statusText(usage.campaigns?.byStatus)}`,
    `${d} drip entr${d === 1 ? 'y' : 'ies'}${statusText(usage.dripEntries?.byStatus)}`,
  ];
  let text = `Used by ${pieces.join(' and ')}.`;
  if (usage.isDripDefault) text += ' It is the drip default layout (DRIP_SENDGRID_TEMPLATE_ID), so every entry without its own template uses it.';
  if (usage.sendingNow) text += ' A campaign using it is sending right now.';
  return text;
}
